/**
 * EditCustomerModal - Edit an existing customer's details
 *
 * Full-featured customer editing form with sections for:
 * - Personal info (name, first/last)
 * - Contact info (phone, email, preferred contact)
 * - Address
 * - Site access & notes
 * - Classification (property type, VIP flag)
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, User, Save, Building2, Copy, Check } from 'lucide-react';
import { Customer } from '@/types';
import { updateCustomer, getCompanies, syncChildrenCompanyName } from '@/services/customer.service';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  onSaved: (updated: Customer) => void;
}

const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

const PROPERTY_TYPES = ['Residential', 'Commercial', 'Industrial', 'Rural', 'Government', 'Other'];

const PREFERRED_CONTACT_OPTIONS = ['Phone', 'Email', 'SMS', 'No Preference'];

export function EditCustomerModal({ isOpen, onClose, customer, onSaved }: Props) {
  const [form, setForm] = useState({
    isCompany: false,
    companyName: '',
    tradingName: '',
    abn: '',
    primaryContactName: '',
    parentCompanyId: '',
    parentCompanyName: '',
    firstName: '',
    lastName: '',
    name: '',
    phone: '',
    phoneSecondary: '',
    email: '',
    preferredContact: '',
    address: '',
    addressLine2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
    propertyType: '',
    isVip: false,
    accessInstructions: '',
    siteNotes: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState<Customer[]>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (customer) {
      setForm({
        isCompany: customer.isCompany || false,
        companyName: customer.companyName || '',
        tradingName: customer.tradingName || '',
        abn: customer.abn || '',
        primaryContactName: customer.primaryContactName || '',
        parentCompanyId: customer.parentCompanyId || '',
        parentCompanyName: customer.parentCompanyName || '',
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        name: customer.name || '',
        phone: customer.phone || '',
        phoneSecondary: customer.phoneSecondary || '',
        email: customer.email || '',
        preferredContact: customer.preferredContact || '',
        address: customer.address || '',
        addressLine2: customer.addressLine2 || '',
        city: customer.city || '',
        state: customer.state || '',
        postcode: customer.postcode || customer.zipCode || '',
        country: customer.country || 'Australia',
        propertyType: customer.propertyType || '',
        isVip: customer.isVip || false,
        accessInstructions: customer.accessInstructions || '',
        siteNotes: customer.siteNotes || '',
        notes: customer.notes || '',
      });
      setError('');
    }
  }, [customer?.id]);

  // Load companies for the parent picker (skip when this record IS a company —
  // a company can't be a child of another company in this model).
  useEffect(() => {
    if (!isOpen) return;
    if (form.isCompany) return;
    getCompanies()
      .then((list) => setCompanies(list.filter((c) => c.id !== customer?.id)))
      .catch(() => setCompanies([]));
  }, [isOpen, form.isCompany, customer?.id]);

  const copyAccountNumber = async () => {
    if (!customer?.accountNumber) return;
    try {
      await navigator.clipboard.writeText(customer.accountNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — silently no-op */
    }
  };

  const handleChange = (field: string, value: string | boolean) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };

      // Keep combined `name` in sync with the relevant source field so list views,
      // searches, and legacy callers work without extra logic.
      if (prev.isCompany) {
        if (field === 'companyName') updated.name = (value as string).trim();
      } else if (field === 'firstName' || field === 'lastName') {
        const first = field === 'firstName' ? (value as string) : prev.firstName;
        const last = field === 'lastName' ? (value as string) : prev.lastName;
        updated.name = [first, last].filter(Boolean).join(' ');
      }

      // When switching type, recompute `name` off the now-active source.
      if (field === 'isCompany') {
        if (value) {
          updated.name = prev.companyName.trim() || prev.name;
        } else {
          updated.name = [prev.firstName, prev.lastName].filter(Boolean).join(' ') || prev.name;
        }
      }

      // Keep parent company denormalized name in sync with the picker.
      if (field === 'parentCompanyId') {
        const match = companies.find((c) => c.id === value);
        updated.parentCompanyName = match ? (match.companyName || match.name || '') : '';
      }

      return updated;
    });
  };

  const handleSave = async () => {
    if (!customer?.id) return;

    const effectiveName = form.isCompany ? form.companyName.trim() : form.name.trim();
    if (!effectiveName) {
      setError(form.isCompany ? 'Company name is required' : 'Customer name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Empty string clears Firestore fields (stripping undefined would leave stale values).
      // null explicitly clears the parent link.
      const updates: Record<string, unknown> = {
        isCompany: form.isCompany,
        companyName: form.isCompany ? form.companyName.trim() : '',
        tradingName: form.isCompany ? form.tradingName.trim() : '',
        abn: form.isCompany ? form.abn.trim() : '',
        primaryContactName: form.isCompany ? form.primaryContactName.trim() : '',
        parentCompanyId: form.isCompany ? null : (form.parentCompanyId || null),
        parentCompanyName: form.isCompany ? '' : form.parentCompanyName,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        name: effectiveName,
        phone: form.phone.trim(),
        phoneSecondary: form.phoneSecondary.trim(),
        email: form.email.trim(),
        preferredContact: form.preferredContact,
        address: form.address.trim(),
        addressLine2: form.addressLine2.trim(),
        city: form.city.trim(),
        state: form.state,
        postcode: form.postcode.trim(),
        zipCode: form.postcode.trim(),
        country: form.country.trim() || 'Australia',
        propertyType: form.propertyType,
        isVip: form.isVip,
        accessInstructions: form.accessInstructions.trim(),
        siteNotes: form.siteNotes.trim(),
        notes: form.notes.trim(),
      };

      await updateCustomer(customer.id, updates);

      // If this is a company and the display name changed, propagate the new
      // name down to children's denormalized parentCompanyName (fire-and-forget).
      const nameChanged =
        form.isCompany &&
        (customer.companyName || customer.name || '') !== form.companyName.trim();
      if (nameChanged) {
        syncChildrenCompanyName(customer.id).catch((e) =>
          console.error('Failed to sync children company name:', e),
        );
      }

      onSaved({ ...customer, ...updates } as Customer);
      onClose();
    } catch (err) {
      console.error('Error updating customer:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !customer) return null;

  return (
    <AnimatePresence>
      {/* Stop clicks from bubbling to any parent modal. When this modal is
          nested inside CustomerDetailModal (which closes on a root-level
          click), an unguarded click anywhere in here — a form field, Save,
          the backdrop — bubbled up and unmounted the whole parent, taking
          this modal with it. z-[210] keeps it above the parent's slide-over. */}
      <div
        className="fixed inset-0 z-[210] flex items-center justify-center p-4"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={saving ? undefined : onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-surface border border-theme rounded-2xl shadow-2xl"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-surface border-b border-theme">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-theme-primary">Edit Customer</h2>
                <p className="text-sm text-theme-tertiary">{customer.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-hover rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-theme-muted" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-6">
            {/* Error */}
            {error && (
              <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-500 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Customer Type + Account Number */}
            <Section title="Customer Type">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleChange('isCompany', false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                    !form.isCompany
                      ? 'bg-brand-secondary/15 border-brand-secondary/50 text-theme-primary'
                      : 'bg-surface-input border-theme text-theme-tertiary hover:bg-surface-hover'
                  }`}
                >
                  <User className="w-5 h-5" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">Individual</div>
                    <div className="text-[10px] opacity-70">Person / residential</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleChange('isCompany', true)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                    form.isCompany
                      ? 'bg-brand-secondary/15 border-brand-secondary/50 text-theme-primary'
                      : 'bg-surface-input border-theme text-theme-tertiary hover:bg-surface-hover'
                  }`}
                >
                  <Building2 className="w-5 h-5" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">Company</div>
                    <div className="text-[10px] opacity-70">Umbrella / business</div>
                  </div>
                </button>
              </div>

              {customer?.accountNumber && (
                <div className="mt-3 flex items-center justify-between gap-3 px-4 py-2.5 bg-surface-input border border-theme rounded-lg">
                  <div>
                    <div className="text-[10px] font-bold text-theme-muted uppercase tracking-widest">Account Number</div>
                    <div className="font-mono text-sm text-theme-primary">{customer.accountNumber}</div>
                  </div>
                  <button
                    type="button"
                    onClick={copyAccountNumber}
                    className="p-2 rounded-lg hover:bg-surface-hover text-theme-tertiary hover:text-theme-primary transition-colors"
                    title="Copy"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </Section>

            {/* Company-specific fields */}
            {form.isCompany && (
              <Section title="Company Details">
                <Field
                  label="Company Name"
                  value={form.companyName}
                  onChange={v => handleChange('companyName', v)}
                  placeholder="Acme Pty Ltd"
                  required
                />
                <div className="grid grid-cols-2 gap-4">
                  <Field
                    label="Trading Name"
                    value={form.tradingName}
                    onChange={v => handleChange('tradingName', v)}
                    placeholder="Trading as..."
                  />
                  <Field
                    label="ABN (optional)"
                    value={form.abn}
                    onChange={v => handleChange('abn', v)}
                    placeholder="11-digit ABN"
                  />
                </div>
                <Field
                  label="Primary Contact Name"
                  value={form.primaryContactName}
                  onChange={v => handleChange('primaryContactName', v)}
                  placeholder="e.g. Jane Smith"
                />
              </Section>
            )}

            {/* Parent Company Link — individuals / child sites only */}
            {!form.isCompany && (
              <Section title="Parent Company">
                <div>
                  <label className="block text-xs font-medium text-theme-tertiary mb-1">
                    Link to company (optional)
                  </label>
                  <select
                    value={form.parentCompanyId}
                    onChange={e => handleChange('parentCompanyId', e.target.value)}
                    className="w-full px-3 py-2 bg-surface-input border border-theme rounded-lg text-sm text-theme-primary focus:outline-none focus:ring-1 focus:ring-brand-secondary/50 focus:border-brand-secondary/50 transition-colors"
                  >
                    <option value="">— Standalone customer —</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.companyName || c.name}
                        {c.accountNumber ? ` · ${c.accountNumber}` : ''}
                      </option>
                    ))}
                  </select>
                  {form.parentCompanyId && (
                    <p className="text-[10px] text-theme-muted mt-1">
                      This customer will appear as a site under <span className="font-semibold text-theme-secondary">{form.parentCompanyName}</span>
                    </p>
                  )}
                </div>
              </Section>
            )}

            {/* Personal Information — only shown for individuals */}
            {!form.isCompany && (
              <Section title="Personal Information">
                <div className="grid grid-cols-2 gap-4">
                  <Field
                    label="First Name"
                    value={form.firstName}
                    onChange={v => handleChange('firstName', v)}
                    placeholder="First name"
                  />
                  <Field
                    label="Last Name"
                    value={form.lastName}
                    onChange={v => handleChange('lastName', v)}
                    placeholder="Last name"
                  />
                </div>
                <Field
                  label="Full Name"
                  value={form.name}
                  onChange={v => handleChange('name', v)}
                  placeholder="Full name"
                  required
                />
              </Section>
            )}

            {/* Contact Information */}
            <Section title="Contact Information">
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Phone"
                  value={form.phone}
                  onChange={v => handleChange('phone', v)}
                  placeholder="04XX XXX XXX"
                  type="tel"
                />
                <Field
                  label="Secondary Phone"
                  value={form.phoneSecondary}
                  onChange={v => handleChange('phoneSecondary', v)}
                  placeholder="Optional"
                  type="tel"
                />
              </div>
              <Field
                label="Email"
                value={form.email}
                onChange={v => handleChange('email', v)}
                placeholder="customer@example.com"
                type="email"
              />
              <SelectField
                label="Preferred Contact"
                value={form.preferredContact}
                onChange={v => handleChange('preferredContact', v)}
                options={PREFERRED_CONTACT_OPTIONS}
                placeholder="Select..."
              />
            </Section>

            {/* Address */}
            <Section title="Address">
              <Field
                label="Street Address"
                value={form.address}
                onChange={v => handleChange('address', v)}
                placeholder="123 Main St"
              />
              <Field
                label="Address Line 2"
                value={form.addressLine2}
                onChange={v => handleChange('addressLine2', v)}
                placeholder="Unit, suite, etc."
              />
              <div className="grid grid-cols-3 gap-4">
                <Field
                  label="City / Suburb"
                  value={form.city}
                  onChange={v => handleChange('city', v)}
                  placeholder="Sydney"
                />
                <SelectField
                  label="State"
                  value={form.state}
                  onChange={v => handleChange('state', v)}
                  options={AUSTRALIAN_STATES}
                  placeholder="State"
                />
                <Field
                  label="Postcode"
                  value={form.postcode}
                  onChange={v => handleChange('postcode', v)}
                  placeholder="2000"
                />
              </div>
              <Field
                label="Country"
                value={form.country}
                onChange={v => handleChange('country', v)}
                placeholder="Australia"
              />
            </Section>

            {/* Site Access & Notes */}
            <Section title="Site Access & Notes">
              <TextArea
                label="Access Instructions"
                value={form.accessInstructions}
                onChange={v => handleChange('accessInstructions', v)}
                placeholder="Gate codes, parking, access instructions..."
                hint="Visible to technicians on job cards"
              />
              <TextArea
                label="Site Notes"
                value={form.siteNotes}
                onChange={v => handleChange('siteNotes', v)}
                placeholder="Dogs on property, parking limitations, etc."
              />
              <TextArea
                label="General Notes"
                value={form.notes}
                onChange={v => handleChange('notes', v)}
                placeholder="Any additional notes about this customer..."
              />
            </Section>

            {/* Classification */}
            <Section title="Classification">
              <SelectField
                label="Property Type"
                value={form.propertyType}
                onChange={v => handleChange('propertyType', v)}
                options={PROPERTY_TYPES}
                placeholder="Select..."
              />
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.isVip}
                  onChange={e => handleChange('isVip', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-brand-secondary focus:ring-brand-secondary/50"
                />
                <span className="text-sm text-theme-secondary group-hover:text-theme-primary transition-colors">
                  VIP Customer
                </span>
              </label>
            </Section>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 flex items-center justify-end gap-3 px-6 py-4 bg-surface border-t border-theme">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-5 py-2.5 text-sm font-medium text-theme-secondary hover:text-theme-primary hover:bg-surface-hover rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || (form.isCompany ? !form.companyName.trim() : !form.name.trim())}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-brand-secondary to-amber-500 text-brand-dark font-semibold rounded-xl hover:scale-[1.02] transition-transform shadow-lg shadow-brand-secondary/20 disabled:opacity-50 disabled:hover:scale-100"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Reusable sub-components (local to this file)
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="text-[10px] font-bold text-theme-muted uppercase tracking-widest">{title}</h3>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-theme-tertiary mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-surface-input border border-theme rounded-lg text-sm text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-1 focus:ring-brand-secondary/50 focus:border-brand-secondary/50 transition-colors"
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-theme-tertiary mb-1">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-surface-input border border-theme rounded-lg text-sm text-theme-primary focus:outline-none focus:ring-1 focus:ring-brand-secondary/50 focus:border-brand-secondary/50 transition-colors"
      >
        <option value="">{placeholder || 'Select...'}</option>
        {options.map(opt => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextArea({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-theme-tertiary mb-1">{label}</label>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2 bg-surface-input border border-theme rounded-lg text-sm text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-1 focus:ring-brand-secondary/50 focus:border-brand-secondary/50 transition-colors resize-none"
      />
      {hint && <p className="text-[10px] text-theme-muted mt-1">{hint}</p>}
    </div>
  );
}
